import zipObject from 'lodash-es/zipObject'

interface PartnerData {
  jwt?: string
  id?: PartnerId
  extra?: string
  directEncashment?: boolean
  language?: string
  salesoffice?: string
  currencies?: string[]
}

type PartnerCookies = {
  jwt?: MaybeRef<string | null | undefined>
  partner?: MaybeRef<PartnerCookiesPartner | null | undefined>
}

type PartnerCookiesPartner = { id?: PartnerId; extra?: string }

export const usePartnerStore = defineStore('partner', () => {
  const params = useParams()
  const cookies = usePartnerCookies()

  const jwt = ref('')
  const id = ref('')
  const extra = ref('')
  const directEncashment = ref(false)
  const language = ref('')
  const salesoffice = ref('')
  const currency = ref('')

  const isB2B = computed(() => !!id.value && !!jwt.value)

  async function init() {
    const partner = await getPartner(
      params.persistent,
      cookies,
      useRequestHeaders(),
    )

    jwt.value = partner.jwt ?? ''
    id.value = partner.id ?? ''
    extra.value = partner.extra ?? ''
    directEncashment.value = partner.directEncashment ?? false
    language.value = partner.language ?? ''
    salesoffice.value = partner.salesoffice ?? ''
    currency.value = partner.currencies?.[0] ?? 'EUR'

    cookies.update(partner)

    if (partner.id) params.persist('partnerid', partner.id)
    if (partner.id && partner.extra)
      params.persist('partnerextra', partner.extra)
  }

  return {
    jwt,
    id,
    extra,
    directEncashment,
    salesoffice,
    currency,
    language,
    isB2B,
    init,
  }
})

function usePartnerCookies() {
  const options = {
    secure: true,
    sameSite: 'none' as const,
    path: '/',
    httpOnly: true,
  }

  const jwt = useCookie('jwt', {
    ...options,
    expires: hoursAdd(new Date(), 12),
  })

  const partner = useCookie<PartnerCookiesPartner | null | undefined>(
    'partner',
    {
      ...options,
      expires: daysAdd(new Date(), 30),
      decode: (raw) => zipObject(['id', 'extra'], raw.split('&') ?? []),
      encode: (value) =>
        value?.id ? [value.id, value?.extra].filter(Boolean).join('&') : '',
    },
  )

  // Sanitizing `partner` cookie
  const validPartnerId = getPartnerId(partner.value?.id)
  partner.value = validPartnerId
    ? { ...partner.value, id: validPartnerId }
    : undefined

  function update({ jwt: _jwt, id, extra }: PartnerData) {
    if (_jwt) jwt.value = _jwt
    else if (id) partner.value = { id, extra }
  }

  return { jwt, partner, update }
}

/**
 * Gets partner data by checking it in order:
 * 1. JWT
 * 2. URL Parameters
 * 3. Referrer
 * 4. Cookie
 *
 * @see https://jira.migros.net/browse/HHDWEBCC-4617
 */
async function getPartner(
  params: PlainParams,
  cookies: PartnerCookies,
  headers: Record<string, string>,
): Promise<PartnerData> {
  const jwt = toValue(cookies.jwt) || params.jwt?.toString()

  if (jwt) return await getPartnerFromJwt(jwt)

  const [id, extra] = [params.partnerid, params.partnerextra].map(
    (value) => value?.toString() || undefined,
  )
  const partner = { id, extra }
  if (!partner.id || !isPartnerIdWhitelisted(partner.id)) {
    partner.id = getPartnerIdForReferrer(headers.referer) ?? partner.id
  }

  return partner.id ? partner : (toValue(cookies.partner) ?? {})
}

async function getPartnerFromJwt(jwt: string) {
  const { data } = await useRetailPortalApi().partner(jwt)

  return { ...(data.value ?? {}), jwt }
}
